/**
 * `contract-links` and `set-contract-link` — an Admin fixes a contract's links
 * after it was filed · CLM-120 (D-110-4 point 5; FR-08, DR-02, DR-03, NFR-06,
 * NFR-07; FR-35 as CLM-120 notes it). The decision is D-110-4 point 5 (Harnoor
 * Gandhi, 7 Oct 2026, Jira CLM-110 comment 1943529, "5a"); the refinements
 * R-120-1 … R-120-21 are the spec's (`phase4-specs/CLM-120-fix-links-after-upload.md`).
 *
 * `GET /v1/contracts/{contract_key}/links` and `POST` on the same path, both
 * gate `admin:configure` (R-120-1: the same gate as the type decision and the
 * dismissal beside it). One request is one audited act, in one transaction.
 *
 * ## What each link allows (R-120-2)
 *
 * | Link | Set | Change | Remove |
 * |---|---|---|---|
 * | `child_of` — its parent MSA | yes | yes | yes (`target_contract_key: null`) |
 * | `amends` — the agreement it amends | yes | yes (re-point) | **no** (R-120-3) |
 * | `supersedes` — the agreement it replaces | yes | no | **no** — Rule 2 is one-way |
 *
 * The rules are intake's (R-120-4): `validRelationships`' pairs, an agreement
 * that is current and `Executed` or `Active` as the target (D-124-1), and the
 * party rule. An amendment follows its agreement (R-120-5): its type and
 * confirmation, owner, classification, category, other parties and restriction
 * (§4C, widening only — R-120-7). A replacement fires Rule 2's flip and keeps its
 * own lifecycle (R-120-8).
 *
 * ## `seen` is the attestation (the `decide-contract-type` idiom)
 *
 * The request names what the Admin looked at — the contract's three edges, its
 * type and confirmation, and the chosen target's type, confirmation, lifecycle
 * and restriction — and the server compares it with the rows it locked: any
 * difference is `409 stale_view`. So an act applies only from the state it
 * names, and a retry after a lost response is `stale_view`, with no idempotency
 * key.
 *
 * ## Scope
 *
 * Every statement is scoped on `root_contract_key` (rules 9, 10). The Admin must
 * see the contract, the new target and the old one; each missing one is `404
 * contract_not_found`, never 403. The GET names an out-of-scope contract only
 * where the contract's **own** row does — its parent, the agreement it amends,
 * the agreement it replaces — with `in_scope: false` and nothing else; the rows
 * pointing **at** it (its replacement, its amendments, its children) are read
 * under the predicate and are absent when unseen (R-120-11). The client sends no
 * UPN, role or scope: every request is `.strict()`.
 */
import { z } from 'zod';
import { AgreementFamilyIdSchema, ContractKeySchema, IsoDateSchema, PartyKeySchema, VersionNoSchema, } from '../primitives.js';
import { AuditReceiptSchema } from '../provenance.js';
import { ContractTypeConfirmationSchema, ContractTypeSchema, LifecycleStatusSchema } from '../vocabulary.js';
/** The three §8.1 edges an Admin may fix (DR-02: lineage is in the columns). */
export const CONTRACT_LINKS = ['child_of', 'amends', 'supersedes'];
export const ContractLinkSchema = z.enum(CONTRACT_LINKS);
/**
 * Every refusal, as the first `field_errors` message's `code:` prefix, in the
 * order the planner checks them (spec §4.3 (c)). `404 contract_not_found` (the
 * contract, the target or the old target, absent or out of scope); `400
 * no_change`, `relationship_not_valid`, `party_follows_agreement` and
 * `relationship_cycle`; `409` every other: `stale_view`, `superseded_read_only`
 * (Rule 3), `amendment_follows_agreement`, `supersession_is_one_way` (Rule 2),
 * `has_amendments`, `has_children` (R-120-6), `current_base_contract`
 * (R-120-12), `agreement_restricted_contract_read` (§4C, R-120-7),
 * `pipeline_run_active` (R-120-7), `links_changed` (R-120-14) and
 * `lineage_outside_scope` (a contract the Admin cannot see sits beneath one being
 * moved — R-120-6, R-120-11).
 */
export const CONTRACT_LINK_REFUSALS = [
    'contract_not_found',
    'stale_view',
    'superseded_read_only',
    'no_change',
    'amendment_follows_agreement',
    'supersession_is_one_way',
    'relationship_not_valid',
    'party_follows_agreement',
    'relationship_cycle',
    'has_amendments',
    'has_children',
    'current_base_contract',
    'agreement_restricted_contract_read',
    'pipeline_run_active',
    'links_changed',
    'lineage_outside_scope',
];
export const ContractLinkRefusalSchema = z.enum(CONTRACT_LINK_REFUSALS);
/**
 * One end of a link. `in_scope: false` only for the contract's own three edge
 * columns (R-120-11): the key is the contract's own column, so it is named, and
 * nothing else about the other row is.
 */
export const ContractLinkRefSchema = z
    .object({
    contract_key: ContractKeySchema,
    in_scope: z.boolean(),
    contract_type: ContractTypeSchema.nullable(),
    contract_type_confirmation: ContractTypeConfirmationSchema.nullable(),
    lifecycle_status: LifecycleStatusSchema.nullable(),
})
    .strict()
    .refine((ref) => (ref.contract_type === null) === !ref.in_scope &&
    (ref.contract_type_confirmation === null) === !ref.in_scope &&
    (ref.lifecycle_status === null) === !ref.in_scope, { message: 'type, confirmation and lifecycle are given exactly when the contract is in scope', path: ['in_scope'] });
/** What the Admin saw of the contract itself — compared with the locked row (`409 stale_view`). */
export const ContractLinkSubjectSeenSchema = z
    .object({
    parent_contract_key: ContractKeySchema.nullable(),
    amends_contract_key: ContractKeySchema.nullable(),
    supersedes_contract_key: ContractKeySchema.nullable(),
    contract_type: ContractTypeSchema,
    contract_type_confirmation: ContractTypeConfirmationSchema,
})
    .strict();
/** What the Admin saw of the chosen target — everything an effect is computed from. */
export const ContractLinkTargetSeenSchema = z
    .object({
    contract_key: ContractKeySchema,
    contract_type: ContractTypeSchema,
    contract_type_confirmation: ContractTypeConfirmationSchema,
    lifecycle_status: LifecycleStatusSchema,
    processing_restricted: z.boolean(),
})
    .strict();
export const ContractLinkSeenSchema = ContractLinkSubjectSeenSchema.extend({
    /** `null` exactly when no target is named — removing a parent MSA. */
    target: ContractLinkTargetSeenSchema.nullable(),
}).strict();
/* -------------------------------------------------------------------------- */
/* Effects — what a link will change, shown before it is saved                 */
/* -------------------------------------------------------------------------- */
/** The effect kinds, in the order the planner lists them. */
export const CONTRACT_LINK_EFFECT_KINDS = [
    'type_follows_agreement',
    'sow_number_taken',
    'sow_number_cleared',
    'parent_cleared',
    'copies_from_agreement',
    'restriction_applied',
    'parties_kept',
    'amendment_flag_cleared',
    'agreement_superseded',
    'base_superseded',
    'stays_with_superseded',
    'hierarchy_moved',
    'typed_key_names_other_type',
    'deviation_reports_kept',
];
export const ContractLinkEffectKindSchema = z.enum(CONTRACT_LINK_EFFECT_KINDS);
const TypeWithConfirmationSchema = z
    .object({ contract_type: ContractTypeSchema, contract_type_confirmation: ContractTypeConfirmationSchema })
    .strict();
/**
 * One effect (R-120-5 … R-120-20). `amends`: the type and confirmation follow the
 * agreement; the SOW number is taken or cleared; a parent MSA link is cleared;
 * owner, classification, category and the agreement's other parties are copied;
 * the restriction is applied (§4C); the contract's own parties the agreement
 * lacks stay on the row but stop deciding who sees it; the "possibly an
 * amendment" flag clears; a typed ID names another type; deviation reports are
 * kept. `supersedes`: the target becomes `Superseded`; a current base is
 * superseded; the target's children and amendments stay with it. `child_of`: the
 * rows beneath move in the hierarchy.
 */
export const ContractLinkEffectSchema = z.discriminatedUnion('kind', [
    z
        .object({ kind: z.literal('type_follows_agreement'), from: TypeWithConfirmationSchema, to: TypeWithConfirmationSchema })
        .strict(),
    z.object({ kind: z.literal('sow_number_taken'), sow_number: z.string().min(1).max(32) }).strict(),
    z.object({ kind: z.literal('sow_number_cleared'), sow_number: z.string().min(1).max(32) }).strict(),
    z.object({ kind: z.literal('parent_cleared'), contract_key: ContractKeySchema }).strict(),
    z.object({ kind: z.literal('copies_from_agreement'), parties_to_copy: z.array(PartyKeySchema) }).strict(),
    z.object({ kind: z.literal('restriction_applied'), reason: z.string().max(2000).nullable() }).strict(),
    z.object({ kind: z.literal('parties_kept'), party_keys: z.array(PartyKeySchema).min(1) }).strict(),
    z.object({ kind: z.literal('amendment_flag_cleared') }).strict(),
    z.object({ kind: z.literal('agreement_superseded'), contract_key: ContractKeySchema }).strict(),
    z.object({ kind: z.literal('base_superseded'), contract_type: ContractTypeSchema }).strict(),
    z
        .object({
        kind: z.literal('stays_with_superseded'),
        children: z.array(ContractKeySchema),
        amendments: z.array(ContractKeySchema),
    })
        .strict(),
    z.object({ kind: z.literal('hierarchy_moved'), contract_keys: z.array(ContractKeySchema).min(1) }).strict(),
    z.object({ kind: z.literal('typed_key_names_other_type'), key_type: z.string().min(3).max(12) }).strict(),
    z.object({ kind: z.literal('deviation_reports_kept'), count: z.number().int().min(1) }).strict(),
]);
/* -------------------------------------------------------------------------- */
/* GET /v1/contracts/{contract_key}/links                                      */
/* -------------------------------------------------------------------------- */
/**
 * The path's `contract_key`, and the optional by-key lookup (R-120-18): with
 * `target_contract_key` the candidate list is that one contract, read under the
 * predicate and planned the same way — the way to reach a target the upload's
 * 100-row cap leaves out. An absent or out-of-scope key is no candidate.
 */
export const ContractLinksRequestSchema = z
    .object({
    contract_key: ContractKeySchema,
    target_contract_key: ContractKeySchema.optional(),
})
    .strict();
/** Whether the contract can take a link at all, and the planner's first refusal when it cannot. */
export const ContractLinkAvailabilitySchema = z
    .object({ offered: z.boolean(), refusal: ContractLinkRefusalSchema.nullable() })
    .strict()
    .refine((entry) => entry.offered === (entry.refusal === null), {
    message: 'an action is offered exactly when it has no refusal',
    path: ['refusal'],
});
export const ContractLinkCandidateSchema = z
    .object({
    contract_key: ContractKeySchema,
    /** `candidateDisplayLabel` — the upload's own label, so the two pickers cannot drift. */
    label: z.string().min(1).max(200),
    contract_type: ContractTypeSchema,
    contract_type_confirmation: ContractTypeConfirmationSchema,
    lifecycle_status: LifecycleStatusSchema,
    /** The fifth field of `seen.target`, so the dialog posts back exactly what it was shown. */
    processing_restricted: z.boolean(),
    effective_date: IsoDateSchema.nullable(),
    /** The links the POST would accept against this candidate, each with its effects. */
    offers: z.array(z.object({ link: ContractLinkSchema, effects: z.array(ContractLinkEffectSchema) }).strict()),
    /** The links it would refuse, each with the planner's code. */
    withheld: z.array(z.object({ link: ContractLinkSchema, refusal: ContractLinkRefusalSchema }).strict()),
})
    .strict()
    .refine((candidate) => {
    const links = [...candidate.offers.map((offer) => offer.link), ...candidate.withheld.map((entry) => entry.link)];
    return links.length === CONTRACT_LINKS.length && CONTRACT_LINKS.every((link) => links.includes(link));
}, { message: 'a candidate’s offered and withheld links are disjoint and cover all three', path: ['offers'] });
export const ContractLinksResponseSchema = z
    .object({
    contract_key: ContractKeySchema,
    contract_type: ContractTypeSchema,
    contract_type_confirmation: ContractTypeConfirmationSchema,
    lifecycle_status: LifecycleStatusSchema,
    primary_party_key: PartyKeySchema.nullable(),
    links: z
        .object({
        parent: ContractLinkRefSchema.nullable(),
        amends: ContractLinkRefSchema.nullable(),
        supersedes: ContractLinkRefSchema.nullable(),
        superseded_by: ContractLinkRefSchema.nullable(),
        amendments: z.array(ContractLinkRefSchema),
        children: z.array(ContractLinkRefSchema),
    })
        .strict(),
    available: z
        .object({
        child_of_set: ContractLinkAvailabilitySchema,
        child_of_remove: ContractLinkAvailabilitySchema,
        amends: ContractLinkAvailabilitySchema,
        supersedes: ContractLinkAvailabilitySchema,
    })
        .strict(),
    candidates: z.array(ContractLinkCandidateSchema),
    /** The upload's 100-row cap bit: more candidates exist than are listed — use `target_contract_key`. */
    candidates_truncated: z.boolean(),
    /** The contract's half of the POST's `seen`, posted back unchanged. */
    seen: ContractLinkSubjectSeenSchema,
})
    .strict()
    .refine((body) => [body.links.superseded_by, ...body.links.amendments, ...body.links.children].every((ref) => ref === null || ref.in_scope), {
    message: 'rows pointing at the contract are read under the predicate: an unseen one is absent, never named',
    path: ['links'],
})
    .refine((body) => body.seen.parent_contract_key === (body.links.parent?.contract_key ?? null) &&
    body.seen.amends_contract_key === (body.links.amends?.contract_key ?? null) &&
    body.seen.supersedes_contract_key === (body.links.supersedes?.contract_key ?? null) &&
    body.seen.contract_type === body.contract_type &&
    body.seen.contract_type_confirmation === body.contract_type_confirmation, { message: 'seen is the contract as listed', path: ['seen'] })
    .refine((body) => body.candidates.every((candidate) => candidate.contract_key !== body.contract_key), {
    message: 'a contract is never its own candidate',
    path: ['candidates'],
});
/* -------------------------------------------------------------------------- */
/* POST /v1/contracts/{contract_key}/links                                     */
/* -------------------------------------------------------------------------- */
export const SetContractLinkRequestSchema = z
    .object({
    /** Path parameter — the contract whose link is fixed. */
    contract_key: ContractKeySchema,
    link: ContractLinkSchema,
    /** The new target; `null` only to remove a parent MSA (`child_of`). */
    target_contract_key: ContractKeySchema.nullable(),
    /** Optional; recorded in the act row's `new_value`. */
    note: z.string().min(1).max(500).optional(),
    seen: ContractLinkSeenSchema,
})
    .strict()
    .refine((request) => request.target_contract_key !== null || request.link === 'child_of', {
    message: 'only a parent MSA can be removed: amends and supersedes name a target',
    path: ['target_contract_key'],
})
    .refine((request) => (request.seen.target === null) === (request.target_contract_key === null), {
    message: 'seen.target is given exactly when a target is named',
    path: ['seen', 'target'],
})
    .refine((request) => request.seen.target === null || request.seen.target.contract_key === request.target_contract_key, { message: 'seen.target names the target', path: ['seen', 'target', 'contract_key'] });
export const SetContractLinkResponseSchema = z
    .object({
    contract_key: ContractKeySchema,
    link: ContractLinkSchema,
    target_contract_key: ContractKeySchema.nullable(),
    /** The parent MSA or agreement it had before; `null` on a first link. */
    previous_target_contract_key: ContractKeySchema.nullable(),
    /** The contract's lineage after the act — the key never changes (FR-35). */
    lineage: z
        .object({
        parent_contract_key: ContractKeySchema.nullable(),
        amends_contract_key: ContractKeySchema.nullable(),
        supersedes_contract_key: ContractKeySchema.nullable(),
        root_contract_key: ContractKeySchema,
        agreement_family_id: AgreementFamilyIdSchema,
        version_no: VersionNoSchema,
        is_current_version: z.boolean(),
    })
        .strict(),
    contract_type: ContractTypeSchema,
    contract_type_confirmation: ContractTypeConfirmationSchema,
    /** Every row the act's UPDATE wrote, the contract first, with the columns it set. */
    rows: z
        .array(z.object({ contract_key: ContractKeySchema, columns: z.array(z.string().min(1).max(63)) }).strict())
        .min(1),
    effects: z.array(ContractLinkEffectSchema),
    /** The act row (`Amendment`, `Supersession` or `Update`) — the NFR-07 receipt. */
    audit: AuditReceiptSchema,
})
    .strict()
    .refine((body) => body.rows[0]?.contract_key === body.contract_key, {
    message: 'the rows list the contract first',
    path: ['rows'],
})
    .refine((body) => body.target_contract_key !== null || body.link === 'child_of', {
    message: 'only a parent MSA can be removed',
    path: ['target_contract_key'],
});
//# sourceMappingURL=contract-links.js.map